import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardHeader } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardHeader/DashboardHeader';
import { DashboardMainPanel } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/DashboardMainPanel';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { generatePath, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { DealDropdown } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealDropdown/DealDropdown.tsx';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils.tsx';
import {
	useTranslationCommonUi,
	useTranslationDocuments,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DashboardGrid } from '@dealstack/frontend/shared/layout/DashboardGrid/DashboardGrid';
import { Tree } from '@dealstack/frontend/shared/components/Tree/Tree.tsx';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { useDeal } from '@dealstack/frontend/shared/modules/deal/hooks/useDeal';
import { keepPreviousData } from '@tanstack/react-query';
import { Row, RowSelectionState } from '@tanstack/react-table';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	getClassificationGroup,
	getClassificationId,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/utils';
import { TreeItem } from '@dealstack/frontend/shared/components/Tree/utils';
import {
	convertApiResponseToTree,
	createFolderId,
	toSelectedDocument,
	TreeItemsWithMeta,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/utils';
import { useClassifierGroups } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useClassifierGroups';
import {
	DocumentsLibraryDocumentRes,
	RightPanelAction,
	SelectedDocument,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import { DOCUMENT_LIBRARY_PARAM_KEYS } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/constants';
import { updateURLParam, UpdateURLParam } from '@dealstack/frontend/shared/helper/updateUrlParams';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { RightPanel } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/RightPanel/RightPanel';

const { SELECTED_TREE_ITEM, EXPANDED_TREE_IDS } = DOCUMENT_LIBRARY_PARAM_KEYS;

export const Dashboard = () => {
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [areAllRowsSelected, setAreAllRowsSelected] = useState<boolean>(false);
	const tCommonUi = useTranslationCommonUi();
	const { dealId } = useRequiredParams('dealId');
	const [searchParams, setSearchParams] = useSearchParams();

	const [isUploadActive, setIsUploadActive] = useState(false);
	const tDocuments = useTranslationDocuments();
	const [rightPanelAction, setRightPanelAction] = useState<RightPanelAction>(
		RightPanelAction.NONE
	);
	const [selectedDocument, setSelectedDocument] = useState<SelectedDocument | undefined>();

	const [selectedTreeItem, setSelectedTreeItem] = useState<TreeItemsWithMeta | undefined>(() => {
		const selectedTreeItemParam = searchParams.get(SELECTED_TREE_ITEM);
		return selectedTreeItemParam ? JSON.parse(selectedTreeItemParam) : null;
	});

	const [expandedTreeIds, setExpandedTreeIds] = useState<TreeItem['id'][]>(() => {
		const expandedTreeIdsParam = searchParams.get(EXPANDED_TREE_IDS);
		const expandedTreeIdsParsed = expandedTreeIdsParam
			? JSON.parse(expandedTreeIdsParam)
			: [createFolderId('COMPLETED_DOCUMENTS', 0)];

		return [...expandedTreeIdsParsed];
	});

	const documentsTableDataOperationsRef = useRef<
		TableOperations<{
			relatedToIds: string[];
			updatedAt: { from: string; to: string };
			mipIds: string[];
		}>
	>({});

	// update URL params when tree state changes
	useEffect(() => {
		const newParams = new URLSearchParams(searchParams);
		const updateUrlParams: UpdateURLParam[] = [
			{
				key: SELECTED_TREE_ITEM,
				condition: !!selectedTreeItem,
				value: JSON.stringify(selectedTreeItem),
				newParams,
			},
			{
				key: EXPANDED_TREE_IDS,
				condition: expandedTreeIds.length > 0,
				value: JSON.stringify(expandedTreeIds),
				newParams,
			},
		];
		updateUrlParams.forEach((updateParam) => {
			updateURLParam(updateParam);
		});

		if (newParams.toString() !== searchParams.toString()) {
			setSearchParams(newParams, { replace: true });
		}
	}, [searchParams, selectedTreeItem, expandedTreeIds, setSearchParams]);

	const { treeItemsData, isTreeItemsMenuLoading } = useClassifierGroups(dealId);

	const treeItems = useMemo(
		() =>
			convertApiResponseToTree(
				treeItemsData,
				expandedTreeIds,
				selectedTreeItem?.id ? [selectedTreeItem.id] : [],
				tDocuments
			),
		[treeItemsData, expandedTreeIds, selectedTreeItem, tDocuments]
	);

	const selectTreeItemFnc = useCallback((leaf: TreeItemsWithMeta) => {
		if (leaf.isFolder) {
			setExpandedTreeIds((prev) =>
				prev.includes(leaf.id) ? prev.filter((id) => id !== leaf.id) : [...prev, leaf.id]
			);
		}

		setSelectedTreeItem(() => leaf);
	}, []);

	const handleArchiveDocument = (row: Row<DocumentsLibraryDocumentRes>) => {
		setSelectedDocument(toSelectedDocument(row.original));
		setRightPanelAction(RightPanelAction.ARCHIVE_DOCUMENT);
	};

	/**
	 * the upload wizard found the file already in the deal — close it and open the
	 * document that holds it.
	 */
	const openExistingDocument = useCallback((existingDocument: SelectedDocument) => {
		setSelectedDocument(existingDocument);
		setRightPanelAction(RightPanelAction.EDIT_DOCUMENT);
		setIsUploadActive(false);
	}, []);

	const { data: deal } = useDeal({
		dealId,
		placeholderData: keepPreviousData,
	});

	const classificationId = getClassificationId(selectedTreeItem);
	const classificationGroup = getClassificationGroup(selectedTreeItem);

	const { deals } = useContext(DealContext);
	const selectedDocumentIds = Object.entries(rowSelection)
		.filter(([, selected]) => selected)
		.map(([rowId]) => rowId);

	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const breadcrumbs = useMemo(() => {
		if (inPageFeatureFlag) {
			return [
				{
					name: deal?.name ?? 'Deal',
					url: generatePath(ROUTES.DEAL, { dealId }),
					element: (
						<DealDropdown
							key="deal-dropdown"
							currentDealId={dealId}
							deals={deals ?? []}
						/>
					),
				},
			];
		} else {
			return [
				...makeDealPageBreadcrumbs({
					tCommonUi,
					dealId,
					dealName: deal?.name,
					deals: deals ?? [],
				}),
				{
					name: tDocuments('documents.library.breadcrumb'),
					url: `#`,
				},
			];
		}
	}, [deal?.name, dealId, deals, inPageFeatureFlag, tDocuments, tCommonUi]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	return (
		<BreadcrumbsProvider>
			<PageContainer style={{ overflow: 'hidden' }}>
				<DashboardGrid
					pageTitle={selectedTreeItem?.name || tDocuments('documents.library.header')}
					headerActions={
						<DashboardHeader
							dealId={dealId}
							setIsUploadActive={setIsUploadActive}
							isUploadActive={isUploadActive}
							onViewExistingDocument={openExistingDocument}
						/>
					}
					sidebarContent={
						<Tree items={treeItems ?? []} onItemClick={selectTreeItemFnc} />
					}
					leftSidebarLoading={isTreeItemsMenuLoading}
					rightSidebarOpened={rightPanelAction !== RightPanelAction.NONE}
					rightSidebarContent={
						<RightPanel
							selectedDocument={selectedDocument}
							selectedTreeItem={selectedTreeItem}
							documentsTableDataOperationsRef={documentsTableDataOperationsRef}
							rightPanelAction={rightPanelAction}
							setRightPanelAction={setRightPanelAction}
							dealId={dealId}
							areAllRowsSelected={areAllRowsSelected}
							selectedDocumentIds={selectedDocumentIds}
							treeItems={treeItems}
							isTreeItemsMenuLoading={isTreeItemsMenuLoading}
							classificationId={classificationId}
							classificationGroup={classificationGroup}
						/>
					}
					allowPanelsResizing={true}
					mainContent={
						<DashboardMainPanel
							dealId={dealId}
							rowSelection={rowSelection}
							setRowSelection={setRowSelection}
							setAreAllRowsSelected={setAreAllRowsSelected}
							selectedTreeItem={selectedTreeItem}
							setSelectedTreeItem={setSelectedTreeItem}
							rightPanelAction={rightPanelAction}
							setRightPanelAction={setRightPanelAction}
							setSelectedDocument={setSelectedDocument}
							handleArchiveDocument={handleArchiveDocument}
							setIsUploadActive={setIsUploadActive}
							documentsTableDataOperationsRef={documentsTableDataOperationsRef}
						/>
					}
				/>
			</PageContainer>
		</BreadcrumbsProvider>
	);
};
